/**
 * Canonical formula printer (Requirement 5.3).
 *
 * The printer is deliberately *canonical* rather than source-preserving:
 *
 * - function names are upper case,
 * - no insignificant whitespace is emitted,
 * - parentheses appear only where precedence or associativity requires them,
 * - `$` anchors are preserved exactly,
 * - sheet names are quoted only when they contain characters outside
 *   `[A-Za-z0-9_]` (delegated to `printSheetName`, so the set of names printed
 *   bare is exactly the set the lexer accepts bare).
 *
 * That is what makes the Requirement 5.4/5.5 round trips hold over syntax
 * trees: `parse(print(ast))` equals `ast` for every tree the parser can
 * produce, even though `print(parse(src))` need not equal `src`. The user's
 * original string stays the stored representation, so display fidelity is
 * unaffected.
 *
 * The printer is a pure function with no I/O and no dependency on the parser.
 */

import { colToLabel, printSheetName } from '../a1.js';
import {
  BINARY_PRECEDENCE,
  POSTFIX_PRECEDENCE,
  UNARY_PRECEDENCE,
  isRightAssociative,
  type Ast,
  type RangeNode,
  type RefNode,
} from './ast.js';

/** Binding power of a leaf or a bracketed construct: never needs parentheses. */
const ATOM_PRECEDENCE = 99;

/**
 * Prints a syntax tree as a complete formula, including the leading `=` the
 * grammar requires (`formula := '=' expr`).
 */
export function printFormula(ast: Ast): string {
  return `=${printExpr(ast)}`;
}

/**
 * Prints a syntax tree as a bare expression, without the leading `=`.
 * Used where a fragment is embedded in other text (the formula bar caret
 * rewrites for `F4`, autocomplete previews, error messages).
 */
export function printExpr(ast: Ast): string {
  switch (ast.k) {
    case 'num':
      return printNumber(ast.v);
    case 'str':
      return `"${ast.v.replace(/"/g, '""')}"`;
    case 'bool':
      return ast.v ? 'TRUE' : 'FALSE';
    case 'err':
      return ast.v;
    case 'ref':
      return printRef(ast);
    case 'range':
      return printRange(ast);
    case 'name':
      return ast.name;
    case 'call':
      return `${ast.name.toUpperCase()}(${ast.args.map((arg) => printExpr(arg)).join(',')})`;
    case 'unary':
      return ast.op === '%'
        ? `${operand(ast.arg, POSTFIX_PRECEDENCE)}%`
        : `${ast.op}${operand(ast.arg, UNARY_PRECEDENCE)}`;
    case 'binary': {
      const prec = BINARY_PRECEDENCE[ast.op];
      // The side that must bind *tighter* than the operator is the one opposite
      // the associativity direction.
      const rightAssoc = isRightAssociative(ast.op);
      const left = operand(ast.left, rightAssoc ? prec + 1 : prec);
      const right = operand(ast.right, rightAssoc ? prec : prec + 1);
      return `${left}${ast.op}${right}`;
    }
  }
}

/** Prints a single reference, sheet-qualified when the node carries a sheet. */
export function printRef(ref: RefNode): string {
  const body = `${ref.absCol ? '$' : ''}${colToLabel(ref.col)}${ref.absRow ? '$' : ''}${ref.row + 1}`;
  return ref.sheet === undefined ? body : `${printSheetName(ref.sheet)}!${body}`;
}

/**
 * Prints a range. The sheet qualifier lives on `from` by construction, so the
 * right-hand reference is always printed bare — `Sheet1!A1:B2`, never
 * `Sheet1!A1:Sheet1!B2`. A degenerate range prints both endpoints (`A1:A1`)
 * rather than collapsing to a single reference, because collapsing would turn a
 * `range` node into a `ref` node and break the round trip.
 */
export function printRange(range: RangeNode): string {
  const to: RefNode = {
    k: 'ref',
    absCol: range.to.absCol,
    col: range.to.col,
    absRow: range.to.absRow,
    row: range.to.row,
  };
  return `${printRef(range.from)}:${printRef(to)}`;
}

/**
 * Prints a numeric literal in the shortest form that re-parses to the same
 * value. `String` already gives the shortest round-tripping decimal for finite
 * doubles, and every form it produces (`0.1`, `100000`, `1e+21`, `5e-324`) is
 * accepted by the tokenizer.
 *
 * The grammar has no notation for a non-finite number, so `Infinity` and `NaN`
 * are rejected rather than printed as something that would not parse back.
 */
function printNumber(v: number): string {
  if (!Number.isFinite(v)) {
    throw new RangeError(`formula numbers must be finite, got ${String(v)}`);
  }
  // Negative literals are printed as a prefix minus, which is how the parser
  // represents them; `Object.is` catches negative zero, whose `String` is '0'.
  if (v < 0 || Object.is(v, -0)) return `-${String(-v)}`;
  return String(v);
}

/**
 * Prints `node` as an operand that must bind at least as tightly as
 * `minPrecedence`, adding parentheses only when it does not.
 */
function operand(node: Ast, minPrecedence: number): string {
  const text = printExpr(node);
  return precedenceOf(node) < minPrecedence ? `(${text})` : text;
}

function precedenceOf(node: Ast): number {
  switch (node.k) {
    case 'binary':
      return BINARY_PRECEDENCE[node.op];
    case 'unary':
      return node.op === '%' ? POSTFIX_PRECEDENCE : UNARY_PRECEDENCE;
    default:
      return ATOM_PRECEDENCE;
  }
}
